import type { Ref } from 'vue';
import type { SaveDraftMode } from '@/components/dialogs/SaveJobDraftDialog.vue';
import type { AfterBook } from '@/composables/jobBookings/useJobBookingSubmit';
import type * as openapi from '@/helpers/api/types/openapiOverrides';
import type {
  BookingStop,
  JobBookingCustomer,
} from '@/models/advancedJobBooking';
import type { PacProduct } from '@/models/pacJobBooking';

import { ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import useJobBookingCustomerData, {
  type RefConfigTuple,
  createBookingStop,
  nextBusinessDayISO,
} from '@/composables/data/useJobBookingCustomerData';
import useJobBookingSaveDraft from '@/composables/jobBookings/useJobBookingSaveDraft';
import useJobBookingSubmit from '@/composables/jobBookings/useJobBookingSubmit';
import {
  fetchGetJobDraftById,
  fetchPostPacJobBookApi,
} from '@/helpers/fetchers';
import { showErrorNotification } from '@/helpers/notifiers';
import { parsePacJobBookingDraft } from '@/helpers/pacJobBooking';
import { reclampDraftStops } from '@/helpers/stopsTimeline';
import * as routeNames from '@/constants/routeNames';
import { createEmptyCustomer } from '@/models/advancedJobBooking';
import {
  buildPacBookApiBody,
  createEmptyPacProduct,
} from '@/models/pacJobBooking';

export interface PacJobBookingViewComposable {
  customer: Ref<JobBookingCustomer>;
  stops: Ref<BookingStop[]>;
  products: Ref<PacProduct[]>;
  attachments: Ref<File[]>;
  refConfigs: Ref<RefConfigTuple>;
  addressItems: Ref<openapi.AddressGETOpenapi[]>;
  addressLoading: Ref<boolean>;
  isSubmitting: Ref<boolean>;
  isLoadingDraft: Ref<boolean>;
  bookingErrors: Ref<string[]>;
  draftId: Ref<number | null>;
  draftName: Ref<string>;
  addProduct: () => void;
  removeProduct: (index: number) => void;
  submit: (after?: AfterBook) => Promise<boolean>;
  saveDraft: (name: string, mode?: SaveDraftMode) => Promise<void>;
  loadDraft: (id: number) => Promise<void>;
  goToDraftsList: () => void;
  reset: () => void;
}

export default function usePacJobBookingView(): PacJobBookingViewComposable {
  const router = useRouter();
  const route = useRoute();
  const { customer, refConfigs, addressItems, addressLoading } =
    useJobBookingCustomerData();

  const stops = ref<BookingStop[]>([
    createBookingStop(1),
    createBookingStop(2),
  ]);
  const products = ref<PacProduct[]>([createEmptyPacProduct()]);
  const attachments = ref<File[]>([]);
  const isSubmitting = ref(false);
  const isLoadingDraft = ref(false);
  const bookingErrors = ref<string[]>([]);
  // Id/name of the draft currently loaded (if any) — lets "Save as draft" update it in place.
  const draftId = ref<number | null>(null);
  const draftName = ref('');

  function addProduct(): void {
    products.value.push(createEmptyPacProduct());
  }

  function removeProduct(index: number): void {
    products.value.splice(index, 1);
  }

  function reset(): void {
    customer.value = createEmptyCustomer();
    stops.value = [createBookingStop(1), createBookingStop(2)];
    products.value = [createEmptyPacProduct()];
    attachments.value = [];
    bookingErrors.value = [];
    draftId.value = null;
    draftName.value = '';
  }

  const { submit } = useJobBookingSubmit({
    stops,
    attachments,
    isSubmitting,
    bookingErrors,
    reset,
    router,
    postBooking: (signal) =>
      fetchPostPacJobBookApi(
        buildPacBookApiBody(customer.value, stops.value, products.value),
        { signal },
      ),
  });

  const { saveDraft } = useJobBookingSaveDraft({
    draftId,
    draftName,
    buildContent: () =>
      buildPacBookApiBody(customer.value, stops.value, products.value),
  });

  async function loadDraft(id: number): Promise<void> {
    isLoadingDraft.value = true;
    try {
      const draft = await fetchGetJobDraftById(id);
      const parsed = parsePacJobBookingDraft(draft.content);
      if (!parsed) {
        showErrorNotification({ title: 'Draft format not supported' });
        return;
      }
      const minDate = nextBusinessDayISO();
      customer.value = parsed.customer;
      stops.value = reclampDraftStops(parsed.stops, minDate);
      products.value = parsed.products;
      bookingErrors.value = [];
      draftId.value = id;
      draftName.value = draft.name;
    } catch {
      // Fetch errors are notified inside the fetcher
    } finally {
      isLoadingDraft.value = false;
    }
  }

  watch(
    () => route.params.id,
    (id) => {
      const routeDraftId = Number(id);
      if (id && !Number.isNaN(routeDraftId)) void loadDraft(routeDraftId);
    },
    { immediate: true },
  );

  function goToDraftsList(): void {
    void router.push({ name: routeNames.DRAFT_JOB_BOOKING });
  }

  return {
    customer,
    stops,
    products,
    attachments,
    refConfigs,
    addressItems,
    addressLoading,
    isSubmitting,
    isLoadingDraft,
    bookingErrors,
    draftId,
    draftName,
    addProduct,
    removeProduct,
    submit,
    saveDraft,
    loadDraft,
    goToDraftsList,
    reset,
  };
}
